'use client';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import type { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import Link from '@eliadmin/core/src/components/link';
import { ButtonIcon } from '../ui/button-icon';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { FetchError } from '@eliadmin/core/src/api/fetcher';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (params: Parameters<typeof newsletterSubscribe>[0]) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(params);
    },
    onSuccess: () => {
      setIsSubscribed(true);
      const tracking = {
        form_id: 'newsletter',
        form_name: 'newsletter_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: 2,
        send_to: generalLayout.marketing?.gtm_id,
      };
      // A tracking chunk failure must not turn a successful subscription into a failed mutation.
      void import('@eliadmin/core/src/components/analytics/analytics-helpers')
        .then(({ trackFormSubmitSuccess }) => trackFormSubmitSuccess(tracking))
        .catch(error => console.error('Newsletter analytics could not be loaded', error));
    },
    // A server outage or a network error used to be reported as "already subscribed".
    onError: error => {
      const isServerOrNetworkError =
        !(error instanceof ValidationError) &&
        (!(error instanceof FetchError) || error.status >= 500);
      if (isServerOrNetworkError) {
        toast({ variant: 'destructive', title: t('text594'), description: t('text530') });
        return;
      }
      setIsAlreadySubscribed(true);
    },
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    setIsAlreadySubscribed(false);
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  const isDesktop = useBreakpoint('sm');

  return (
    <form
      className={cn(
        'flex w-full flex-col items-start justify-start gap-4 rounded-[12px] bg-white p-6 lg:p-8',
        'ea-homepage-newsletter__form',
      )}
      onSubmit={handleSubmit}
    >
      <div className="flex w-full flex-col items-start justify-start gap-1 sm:gap-2">
        <Heading tag="h2" styleAs="h4" title="Newsletter" />
        <Paragraph className="w-full tracking-tight" size="base">
          {t('text3675')}
        </Paragraph>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <Input
          aria-label={systemLabels('email')}
          id="newsletter-email"
          type="email"
          name="email"
          placeholder={t('text3676')}
          onChange={e => setEmail(e.target.value)}
          className={{
            wrapper: cn(
              'h-16 overflow-hidden rounded-full bg-input',
              'ea-homepage-newsletter__form-item-email-input',
            ),
            main: cn(
              'placholder:text-[#9E9E9E] pl-4 text-sm text-black sm:pl-6 lg:p-8 lg:text-base',
            ),
          }}
          after={
            <Button
              type="submit"
              size={isDesktop ? 'default' : 'icon'}
              className={cn('mr-1', 'ea-homepage-newsletter__form-item-email-submit-button')}
              disabled={email.length === 0 || !checked || isSubscribed}
              isLoading={subscribeMutation.isPending}
              aria-label={t('text182')}
            >
              <ButtonIcon
                alt={t('text185')}
                url="/images/icons/send-newsletter-white.svg"
                className="block sm:hidden"
              />
              <p className="hidden sm:block">{t('text182')}</p>
            </Button>
          }
        />
        {isSubscribed && !isAlreadySubscribed && (
          <span className={cn('font-semibold text-success')}>{t('text3139')}</span>
        )}
        {isAlreadySubscribed && (
          // `text-error` is not a Tailwind colour in this project, the message rendered black.
          <span className={cn('font-semibold text-destructive')} role="alert">
            {t('text234')}
          </span>
        )}
      </div>

      <Paragraph className="w-full">
        {t('text3753')}{' '}
        <Link
          prefetch={false}
          target="_blank"
          rel="noopener noreferrer"
          href={'/ochrana-osobnych-udajov'}
          className="underline"
        >
          {t('text614')}
        </Link>{' '}
        {t('text3389')}
      </Paragraph>
    </form>
  );
}
